import { useEffect, useRef, useState } from 'react';
import { Bell, Check, X } from 'lucide-react';
import { fetchAccountEntitlements } from '../lib/api/account';
import { createAlarm } from '../lib/api/alarms';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { AccountEntitlementsResponse, ChartSymbol, PriceAlarm } from '../types/api';
import { useAlarmSetup } from './useAlarmSetup';

interface DesktopAlarmModalProps {
  open: boolean;
  symbol: ChartSymbol | null;
  initialPrice?: number;
  onDraftPriceChange(price: number | null): void;
  onSaved(alarm: PriceAlarm): void;
  onOpenChange(open: boolean): void;
}

export function DesktopAlarmModal({ open, symbol, initialPrice, onDraftPriceChange, onSaved, onOpenChange }: DesktopAlarmModalProps) {
  const setup = useAlarmSetup({ symbol, initialPrice });
  const push = usePushNotifications();
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [entitlements, setEntitlements] = useState<AccountEntitlementsResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => priceInputRef.current?.focus(), 0);
    void refreshEntitlements();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setPriceInput(setup.targetPrice ? formatPrice(setup.targetPrice, symbol?.pricescale, true) : '');
    onDraftPriceChange(setup.targetPrice);
  }, [open, setup.targetPrice, symbol?.pricescale]);

  useEffect(() => {
    if (open) return;
    setSaveError('');
    setSaving(false);
    setEntitlements(null);
    onDraftPriceChange(null);
  }, [open]);

  async function refreshEntitlements() {
    try {
      setEntitlements(await fetchAccountEntitlements());
    } catch {
      setEntitlements(null);
    }
  }

  function setPriceFromInput(value: string) {
    const normalized = value.replace(/,/g, '.');
    setPriceInput(normalized);
    const nextPrice = Number.parseFloat(normalized);
    if (Number.isFinite(nextPrice) && nextPrice > 0) {
      const roundedPrice = roundToPricescale(nextPrice, symbol?.pricescale);
      setup.setTargetPrice(roundedPrice);
      onDraftPriceChange(roundedPrice);
    }
  }

  async function saveAlarm() {
    if (!symbol || !setup.valid || setup.targetPrice === null) return;
    setSaving(true);
    setSaveError('');
    try {
      const alarm = await createAlarm({
        symbol: symbol.name,
        timeframe: setup.timeframe,
        target_price: setup.targetPrice,
        direction: setup.direction,
        trigger_mode: 'latest_quote',
        repeat_mode: 'once',
      });
      onSaved(alarm);
      onOpenChange(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save alarm');
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const directionLabel = setup.direction === 'above' ? 'At or above' : 'At or below';
  const activeAlarmUsage = entitlements?.usage['alarm.active_count'] ?? null;
  const activeAlarmLimit = entitlements?.entitlements['alarm.active_limit']?.limit_value ?? null;
  const activeAlarmLimitReached = activeAlarmLimit !== null && activeAlarmUsage !== null && activeAlarmUsage >= activeAlarmLimit;
  const pushDeviceUsage = entitlements?.usage['alarm.push_device_count'] ?? null;
  const pushDeviceLimit = entitlements?.entitlements['alarm.push_device_limit']?.limit_value ?? null;
  const pushDeviceLimitReached = push.state !== 'subscribed' && pushDeviceLimit !== null && pushDeviceUsage !== null && pushDeviceUsage >= pushDeviceLimit;
  const visibleError = saveError || setup.error || (activeAlarmLimitReached ? 'Active alarm limit reached.' : '');

  return (
    <div className="desktop-alarm-backdrop" onMouseDown={() => onOpenChange(false)}>
      <section
        className="desktop-alarm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-alarm-title"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onOpenChange(false);
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') void saveAlarm();
        }}
      >
        <header className="desktop-alarm-header">
          <div>
            <strong id="desktop-alarm-title">Create price alarm</strong>
            <span>{symbol ? `${symbol.name} · ${symbol.exchange || symbol.description}` : 'Select a symbol'}</span>
          </div>
          <button type="button" className="desktop-alarm-icon-button" aria-label="Close alarm setup" onClick={() => onOpenChange(false)}>
            <X size={18} strokeWidth={2.2} />
          </button>
        </header>

        <label className="desktop-alarm-price-field">
          <span>{directionLabel}</span>
          <input
            ref={priceInputRef}
            inputMode="decimal"
            lang="en-US"
            value={priceInput}
            onChange={(event) => setPriceFromInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === ',') {
                event.preventDefault();
                if (!priceInput.includes('.')) setPriceFromInput(`${priceInput}.`);
              }
              if (event.key === 'Enter') void saveAlarm();
            }}
          />
          {setup.latestPrice !== null && <small>Latest {formatPrice(setup.latestPrice, symbol?.pricescale)}</small>}
        </label>

        <div className="desktop-alarm-push-card">
          <Bell size={18} strokeWidth={2} aria-hidden="true" />
          <div>
            <strong>{pushStatusTitle(push.state)}</strong>
            <span>{pushDeviceLimitReached ? pushLimitMessage(pushDeviceUsage, pushDeviceLimit) : pushStatusMessage(push.state, push.error, pushDeviceUsage, pushDeviceLimit)}</span>
          </div>
          {push.state !== 'subscribed' && push.state !== 'unsupported' && push.state !== 'install-required' && (
            <button type="button" onClick={() => void push.enable().then(refreshEntitlements)} disabled={push.state === 'subscribing' || pushDeviceLimitReached}>
              {push.state === 'subscribing' ? 'Enabling' : 'Enable'}
            </button>
          )}
          {push.state === 'subscribed' && <Check size={18} strokeWidth={2.2} aria-label="Notifications enabled" />}
        </div>

        {visibleError && <div className="desktop-alarm-error">{visibleError}</div>}

        <footer className="desktop-alarm-footer">
          <button type="button" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="button" className="primary" disabled={!setup.valid || saving || activeAlarmLimitReached} onClick={() => void saveAlarm()}>
            {saving ? 'Saving...' : 'Create alarm'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function formatPrice(price: number, pricescale = 100, raw = false): string {
  const decimals = priceDecimals(pricescale);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: raw ? 0 : decimals,
    maximumFractionDigits: decimals,
    useGrouping: !raw,
  }).format(price);
}

function roundToPricescale(price: number, pricescale = 100): number {
  const decimals = priceDecimals(pricescale);
  const factor = 10 ** decimals;
  return Math.round(price * factor) / factor;
}

function priceDecimals(pricescale: number): number {
  if (!Number.isFinite(pricescale) || pricescale <= 1) return 2;
  return Math.min(8, Math.max(0, Math.round(Math.log10(pricescale))));
}

function pushStatusTitle(state: string): string {
  switch (state) {
    case 'subscribed':
      return 'Notifications enabled';
    case 'install-required':
      return 'Install app first';
    case 'permission-denied':
      return 'Notifications blocked';
    case 'unsupported':
      return 'Push unavailable';
    case 'subscribing':
      return 'Enabling notifications';
    case 'error':
      return 'Notification setup failed';
    default:
      return 'Enable push notifications';
  }
}

function pushStatusMessage(state: string, error: string, usage: number | null, limit: number | null): string {
  const suffix = usage !== null && limit !== null ? ` ${usage}/${limit} devices used.` : '';
  switch (state) {
    case 'subscribed':
      return `Alarm will push to this device.${suffix}`;
    case 'install-required':
      return 'On iOS, add Chartify to Home Screen and open it there.';
    case 'permission-denied':
      return 'Allow notifications in browser settings.';
    case 'unsupported':
      return 'This browser does not support Web Push.';
    case 'subscribing':
      return 'Waiting for permission and device subscription.';
    case 'error':
      return error || 'Try enabling notifications again.';
    default:
      return `Needed to receive alarm pushes on this device.${suffix}`;
  }
}

function pushLimitMessage(usage: number | null, limit: number | null): string {
  if (usage === null || limit === null) return 'Push device limit reached.';
  return `${usage}/${limit} push devices used. Disable another device before adding this one.`;
}
